// Thin wrappers over real table elements.
//
// Deliberately not a DataGrid: every list in this app sorts, filters and pages
// on the SERVER (§06), so a grid component that owns those in the client would
// be fighting the API for control of the same state. What is left is a table —
// and a `<table>` is already accessible, already keyboard-navigable, and
// already understands `colspan`.

import type { MouseEvent, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from "react";
import styles from "./Table.module.css";

export const tableStyles = styles;

/**
 * Whether a click on a `.rowLink` row was on the ROW rather than on something
 * inside it. Anything the row contains that is itself a control keeps its own
 * click — a link, a status dropdown, a button — so reaching for one of those
 * never also fires the row's own action.
 *
 * Here rather than inline at each table, because "the row is clickable" is a
 * pattern several screens use and the exclusion list is the whole of what makes
 * it safe.
 */
export function isRowBackgroundClick(event: MouseEvent<HTMLTableRowElement>): boolean {
  const target = event.target;
  return !(target instanceof Element) || target.closest("a,button,select,input,label") === null;
}

function cx(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export interface TableProps {
  /** Names the table for assistive tech — what these rows ARE. */
  label: string;
  /** A screen's own rules for this table — its column widths, how its rows align. */
  className?: string;
  children: ReactNode;
}

export function Table({ label, className, children }: TableProps): ReactNode {
  return (
    <div className={styles.wrap}>
      <table className={cx(styles.table, className)} aria-label={label}>
        {children}
      </table>
    </div>
  );
}

export interface CellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
  muted?: boolean;
  strong?: boolean;
  nowrap?: boolean;
  /** Monospaced tabular figures. Anything that is a number, date or id. */
  mono?: boolean;
}

export function Td({
  numeric,
  muted,
  strong,
  nowrap,
  mono,
  className,
  children,
  ...rest
}: CellProps): ReactNode {
  return (
    <td
      className={cx(
        numeric && styles.numeric,
        muted && styles.muted,
        strong && styles.strong,
        nowrap && styles.nowrap,
        mono && "mono",
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}

export interface HeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
}

export function Th({ numeric, className, children, ...rest }: HeaderCellProps): ReactNode {
  return (
    <th scope="col" className={cx(numeric && styles.numeric, className)} {...rest}>
      {children}
    </th>
  );
}

export interface EmptyRowProps {
  colSpan: number;
  title: string;
  /**
   * Why it is empty and what would fill it. "No results" tells a reader
   * nothing they didn't already know from looking at the blank table.
   */
  description: string;
}

/**
 * An empty state INSIDE the table, so the column headers stay on screen. A
 * reader who filtered their way to zero rows needs to see which filters are
 * still applied — replacing the whole table with a message hides that.
 */
export function EmptyRow({ colSpan, title, description }: EmptyRowProps): ReactNode {
  return (
    <tr>
      <td colSpan={colSpan} className={styles.empty}>
        <div className={styles.emptyTitle}>{title}</div>
        <div
          className={styles.emptyBody}
          {...(typeof description === "string" ? { title: description } : {})}
        >
          {description}
        </div>
      </td>
    </tr>
  );
}
